import styled, { CSSObject, CSSProperties } from 'styled-components';
import { Button } from 'ui/button';
import { FormField } from 'ui/formField';
import { NeutralBanner } from 'ui/banner';
import { ModalBody, ModalHeader } from 'ui/modal';
import { colors, fontSizes, fontWeights, letterSpacing, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';

export const StyledModalHeader = styled(ModalHeader)`
  border-bottom: ${spacing.single} solid ${colors.grey15};
`;

export const StyledModalBody = styled(ModalBody)`
  background: ${colors.white};
`;

export const ListEntryContainer = styled.div`
  padding: ${spacing.base};
  margin-bottom: ${spacing.quarter};

  &.selected {
    color: ${colors.textLink};
    background-color: ${colors.purple10};

    span {
      color: ${colors.purple50};
    }
  }

  cursor: pointer;
`;

const singleLineClamp = `
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const StyledListTitle = styled.div`
  ${singleLineClamp}
`;

export const StyledCaption = styled.span`
  ${({ theme }) => theme.styles.captions.caption1}
  color: ${colors.textSecondary};
  ${singleLineClamp}
`;

export const FormBorder = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.plusHalf};
  padding: ${spacing.plusHalf};

  border: ${spacing.eighth} solid ${colors.brandGreen};
  border-radius: ${spacing.half};
`;

export const DetailFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
`;

export const StyledFormField = styled(FormField)`
  align-items: baseline;
  font-size: ${({ theme }) => theme.styles.fontSizes.s14};
  & + & {
    margin-top: 0;
  }
`;

export const Header = styled.div`
  ${({ theme }) => theme.styles.headings.heading4}

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const OptionalFieldLabel = styled.span`
  color: ${colors.textSecondary};
  font-weight: ${fontWeights.regular};
  margin-left: ${spacing.half};
`;

export const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.plusHalf};
  margin-left: ${spacing.plusHalf};
`;

export const DetailsHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.quarter};
  padding-right: ${spacing.base};
`;

export const DetailsHeaderEditLabel = styled.span`
  white-space: nowrap;
`;

export const CopyContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.base};

  font-size: ${fontSizes.s14};
  color: ${colors.textSecondary};
  margin-top: ${spacing.half};
  margin-bottom: ${spacing.half};
  padding: ${spacing.base};
  border: ${spacing.single} solid ${colors.grey15};
  border-radius: ${spacing.half};
`;

export const CopyButton = styled(Button)`
  flex-shrink: 0;
  width: ${spacing.x6};
  white-space: nowrap;
`;

export const ExpandableContainer = styled.div<{ isExpanded?: boolean }>`
  ${({ isExpanded }) => (isExpanded ? '' : singleLineClamp)}
  padding-right: ${spacing.base};
`;

export const InfoText = styled.span<{ isUnderlined?: boolean }>`
  ${({ isUnderlined }) => (isUnderlined ? 'text-decoration: underline; cursor: pointer;' : '')}
  color: ${colors.info100};
`;

export const StyledNeutralBanner = styled(NeutralBanner)`
  align-items: flex-start;

  [data-test='banner-title'] {
    font-weight: ${fontWeights.bold};
  }
  [data-test='banner-close-ghost-success'] > svg > path {
    stroke: ${colors.textSecondary};
  }
`;

export const ButtonsWrapper = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const FormWrapper = styled.div`
  margin-left: ${spacing.plusHalf};
`;

export const HeroBanner = styled(StyledNeutralBanner)`
  margin-bottom: ${spacing.plusHalf};
  padding: ${spacing.plusHalf};
  [data-test='banner-body'] {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing.double};

    svg {
      flex-shrink: 0;
    }
  }
`;

export const HeroTitle = styled.div`
  ${({ theme }) => theme.styles.headings.heading4}
  margin-bottom: ${spacing.threeQuarter};
`;

export const HeroBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.threeQuarter};
  align-items: flex-start;
`;

export const NeutralLink = styled.a`
  color: inherit;
  text-decoration: underline;
`;

export const PreviewContent = styled.div`
  height: ${spacing.x24};
  margin-top: ${spacing.half};
  padding: ${spacing.plusHalf} ${spacing.double} 0;
  overflow: hidden auto;
  background-color: ${colors.backgroundSecondary};
  border: 1px solid ${colors.grey15};
  border-radius: ${spacing.half};
`;

// Preview styles below
export const messageHeader: CSSProperties = {
  alignItems: `center`,
  backgroundColor: colors.grey1,
  boxSizing: `border-box`,
  display: `flex`,
  flex: `0 0 auto`,
  height: pxToRem(85),
  paddingTop: pxToRem(7),
  width: `100%`,
};

export const messageHeaderCenter: CSSProperties = {
  display: `flex`,
  flex: `1 1 auto`,
  justifyContent: `center`,
};

export const messageBody: CSSProperties | CSSObject = {
  alignItems: `flex-start`,
  boxSizing: `border-box`,
  display: `flex`,
  flex: `1 1 auto`,
  flexDirection: `column`,
  fontWeight: fontWeights.regular,
  overflowX: `hidden`,
  overflowY: `auto`,
  padding: pxToRem(14),
  paddingBottom: 0,
  width: `100%`,
  '::-webkit-scrollbar': {
    display: 'none' /* Chrome, Safari and Opera */,
  },
  '-ms-overflow-style': 'none' /* Edge */,
  'scrollbar-width': 'none' /* Firefox */,
};

export const messageBodyMessageResponder: CSSProperties = {
  borderRadius: pxToRem(7),
  backgroundColor: `${colors.grey5}`,
  color: `${colors.textPrimary}`,
  flex: `0 0 auto`,
  fontSize: pxToRem(12),
  lineHeight: 1.3,
  margin: `${pxToRem(7)} 0px`,
  maxWidth: pxToRem(240),
  overflowWrap: `break-word`,
  padding: `${pxToRem(6)} ${pxToRem(12)}`,
  whiteSpace: `pre-wrap`,
};

export const messageBodyMessageSender: CSSProperties = {
  ...messageBodyMessageResponder,
  backgroundColor: `${colors.green20}`,
  alignSelf: 'flex-end',
};

export const messageBodyMessageLabelResponder: CSSProperties = {
  color: colors.textSecondary,
  flex: `0 0 auto`,
  fontSize: pxToRem(12),
  lineHeight: 1.3,
};

export const messageBodyMessageLabelSender: CSSProperties = {
  ...messageBodyMessageLabelResponder,
  alignSelf: 'flex-end',
};

export const deviceContainer: CSSProperties = {
  alignItems: `center`,
  borderTopRightRadius: pxToRem(20),
  borderTopLeftRadius: pxToRem(20),
  background: `${colors.white}`,
  border: `2px solid ${colors.grey25}`,
  borderBottom: 'none',
  display: `flex`,
  flexDirection: `column`,
  letterSpacing: letterSpacing.pos10,
  margin: `auto`,
  overflow: `hidden auto`,
  width: pxToRem(300),
  height: '100%',
};
